/**
 * useBusinessVerify — 사업자등록번호 국세청 진위확인 훅
 *
 * 사용:
 *   const biz = useBusinessVerify(businessNumberValue);
 *   biz.onBlur()         // 입력 칸에서 포커스 떠날 때 호출 → API 검증 시작
 *   biz.status           // 'idle' | 'checking' | 'valid' | 'closed' | 'not_found' | 'unknown' | 'invalid_format'
 *   biz.message          // 사용자에게 보여줄 메시지
 *   biz.isError          // 빨간 글씨로 표시할지 (not_found 또는 closed 시 true)
 *
 * 흐름:
 *   1. 빈 값이거나 포맷이 안 맞으면 → idle (검증 안 함)
 *   2. blur 시점에 10자리 형식이면 → checking → API 호출
 *   3. 같은 번호 중복 검증 방지 (lastChecked 기억)
 *   4. API 응답:
 *      - valid_only / valid → 정상 (status=valid, isError=false)
 *      - closed             → 폐업 (status=closed, isError=true, 경고 메시지)
 *      - not_found          → 미등록 (status=not_found, isError=true, "국세청 미등록" 메시지)
 *      - unknown            → API 장애 (status=unknown, isError=false, 검증 통과로 처리)
 *      - mismatch           → 회사명/대표자명 불일치 (현재 사용 안 함, 향후 도입 시)
 */
import { useState, useCallback, useRef } from 'react';
import {
  verifyBusinessNumber,
  type VerifyBusinessResponse,
} from '@/utils/verifyBusinessNumber';

export type BizVerifyStatus =
  | 'idle'             // 검증 안 함 (빈 값 등)
  | 'invalid_format'   // 형식 오류 (10자리 아님)
  | 'checking'         // API 호출 중
  | 'valid'            // 정상 사업자
  | 'closed'           // 폐업
  | 'not_found'        // 미등록 (가짜)
  | 'mismatch'         // 회사명/대표자명 불일치 (향후)
  | 'unknown';         // API 장애

export interface UseBusinessVerifyResult {
  status: BizVerifyStatus;
  message: string;
  isError: boolean;
  onBlur: () => void;
  reset: () => void;
}

/** 사업자등록번호 형식 정규식 (10자리, 하이픈 허용) */
const BIZ_NUMBER_RE = /^\d{3}-?\d{2}-?\d{5}$/;

export function useBusinessVerify(
  /** 현재 입력 중인 사업자번호 값 (form watch 값) */
  value: string | undefined,
): UseBusinessVerifyResult {
  const [status, setStatus] = useState<BizVerifyStatus>('idle');
  const [message, setMessage] = useState<string>('');

  // 같은 번호 중복 호출 방지용 — 마지막으로 검증 완료한 번호 기억
  const lastCheckedRef = useRef<string>('');

  /** blur 이벤트에서 호출 — 형식 OK면 API 검증 */
  const onBlur = useCallback(async () => {
    const raw = (value || '').trim();

    // 빈 값 → 검증 스킵
    if (!raw) {
      setStatus('idle');
      setMessage('');
      return;
    }

    // 형식 검증 (zod 스키마에서도 잡지만 여기서 한 번 더)
    if (!BIZ_NUMBER_RE.test(raw)) {
      // 형식 에러는 zod가 빨간 글씨로 표시하므로 여기선 idle로 두고 무시
      setStatus('idle');
      setMessage('');
      return;
    }

    // 같은 번호 중복 검증 방지
    if (raw === lastCheckedRef.current) {
      return;
    }
    lastCheckedRef.current = raw;

    // API 호출
    setStatus('checking');
    setMessage('국세청에 확인 중입니다...');

    const result: VerifyBusinessResponse = await verifyBusinessNumber(raw);

    switch (result.status) {
      case 'valid':
      case 'valid_only':
        setStatus('valid');
        setMessage('국세청에 등록된 사업자입니다.');
        break;
      case 'closed':
        setStatus('closed');
        setMessage('폐업 처리된 사업자번호입니다. 확인 후 다시 입력해 주세요.');
        break;
      case 'not_found':
        setStatus('not_found');
        setMessage('국세청에 등록되지 않은 사업자번호입니다.');
        break;
      case 'mismatch':
        setStatus('mismatch');
        setMessage(result.message || '회사명 또는 대표자명이 일치하지 않습니다.');
        break;
      case 'unknown':
      default:
        // API 장애 — 검증 통과로 처리 (정상 고객 차단 방지)
        setStatus('unknown');
        setMessage('');
        break;
    }
  }, [value]);

  /** 외부에서 강제 초기화할 때 (예: 폼 리셋 시) */
  const reset = useCallback(() => {
    setStatus('idle');
    setMessage('');
    lastCheckedRef.current = '';
  }, []);

  const isError = status === 'not_found' || status === 'closed' || status === 'mismatch';

  return { status, message, isError, onBlur, reset };
}
